import React from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Header,
  Icon,
  Modal
} from 'semantic-ui-react';
import { commitMutation, graphql } from 'react-relay';
import { GlobalNotifications, Intent } from '../../../Modules';
import ClipboardButton from 'react-clipboard.js';
import SelectSigner from './SelectSigner';
import RequestSignature from './RequestSignature';
import EmailSuccess from './EmailSuccess';

export default class ContractShare extends React.Component {
  static propTypes = {
    document: PropTypes.object.isRequired,
    trigger: PropTypes.node.isRequired,
    relay: PropTypes.object.isRequired,
    onOpen: PropTypes.func,
    onClose: PropTypes.func
  };

  constructor(props) {
    super(props);
    this.state = {
      open: false,
      step: 0,
      to: '',
      cc: '',
      subject: this.props.document.title,
      message: ''
    };
    this.steps = ['SelectSigner', 'requestSignature', 'emailSuccess'];
    this.onOpen = this.onOpen.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onSuccessfulCopy = this.onSuccessfulCopy.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onTextChange = this.onTextChange.bind(this);
    this.onEmailChange = this.onEmailChange.bind(this);
    this.getEmailSuggestions = this.getEmailSuggestions.bind(this);
    this.currentStep = this.currentStep.bind(this);
    this.next = this.next.bind(this);
    this.previous = this.previous.bind(this);
    this.emailSignLink = this.emailSignLink.bind(this);
    this.selectSignatureBlock = this.selectSignatureBlock.bind(this);
  }

  onOpen() {
    this.setState({ open: true, step: 0 });
    if (this.props.onOpen) this.props.onOpen();
  }

  onClose() {
    this.setState({ open: false });
    if (this.props.onClose) this.props.onClose();
  }

  onSuccessfulCopy() {
    GlobalNotifications.show({
      message: 'Successfully copied URL',
      intent: Intent.SUCCESS
    });
  }

  onFocus(e) {
    e.target.select();
  }

  onEmailChange(e, { value, name }) {
    this.setState({ [name]: value });
  }

  onTextChange(e) {
    this.setState({ [e.target.name]: e.target.value });
  }

  getWorkspaceContactEmails() {
    const workspaces = this.props.document.workspaces.edges;
    const contacts = [];
    if (workspaces.length > 0) {
      const userContacts = workspaces[0].node.event.user_contacts.edges;
      userContacts.map(({ node: uc }) =>
        uc.user_contact_emails.edges.map(({ node: email }) => {
          contacts.push({
            title: `${uc.first_name} ${uc.last_name}`,
            description: email.address,
            value: email.address
          });
          return email;
        })
      );
    }
    return contacts;
  }

  getEmailSuggestions() {
    const { email, name } = this.state.selectedSignatureBlock;
    const emails = this.getWorkspaceContactEmails();
    if (email) {
      emails.unshift({ title: name, description: email, value: email });
    }
    return emails;
  }

  currentStep() {
    return this.steps[this.state.step];
  }

  hasNextStep() {
    return this.state.step < this.steps.length - 1;
  }

  hasPreviousStep() {
    return this.state.step > 0;
  }

  next() {
    if (this.hasNextStep) {
      this.setState((prevState) => ({ step: prevState.step + 1 }));
    }
  }

  previous() {
    if (this.hasPreviousStep) {
      this.setState((prevState) => ({ step: prevState.step - 1 }));
    }
  }

  selectSignatureBlock(idx) {
    const selectedSignatureBlock = this.props.document.contract_signature_blocks
      .edges[idx].node;
    if (!selectedSignatureBlock.signed_at) {
      this.setState({ selectedSignatureBlock, to: selectedSignatureBlock.email || '' });
    }
  }

  emailSignLink() {
    const onCompleted = () => {
      this.next();
    };

    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to send email',
        intent: Intent.DANGER
      });
    };

    const mutation = graphql`
      mutation Share_emailContractSignLinkMutation(
        $input: EmailContractSignLinkInput!
      ) {
        emailContractSignLink(input: $input) {
          sent_email_to
          sent_email_cc
        }
      }
    `;

    const environment = this.props.relay.environment;
    const csbId = this.state.selectedSignatureBlock.id;

    const { to, subject, message } = this.state;
    const cc = this.state.cc === '' ? null : this.state.cc;

    commitMutation(environment, {
      mutation,
      variables: { input: { csb_id: csbId, to, cc, subject, message } },
      onCompleted,
      onError
    });
  }

  render() {
    const { to, cc, subject, message, selectedSignatureBlock } = this.state;
    const { document, trigger } = this.props;
    const copyButton = url =>
      <ClipboardButton
        option-text={() => url}
        onSuccess={this.onSuccessfulCopy}
        className='ui right icon button button'
      >
        <span>
          Copy &nbsp;<Icon name='copy outline' />
        </span>
      </ClipboardButton>;

    return (
      <Modal
        trigger={trigger}
        closeIcon
        onClose={this.onClose}
        onOpen={this.onOpen}
        open={this.state.open}
        size='small'
      >
        <Header content='Share Contract' />
        <Modal.Content>
          <Grid padded relaxed stackable centered>
            {this.currentStep() === 'SelectSigner' &&
              <SelectSigner
                copyButton={copyButton}
                document={document}
                next={this.next}
                onFocus={this.onFocus}
                selectSignatureBlock={this.selectSignatureBlock}
                selectedSignatureBlock={selectedSignatureBlock}
              />}
            {this.currentStep() === 'requestSignature' &&
              <RequestSignature
                to={to}
                cc={cc}
                subject={subject}
                message={message}
                emailSuggestions={this.getEmailSuggestions()}
                emailSignLink={this.emailSignLink}
                copyButton={copyButton}
                onClose={this.onClose}
                onFocus={this.onFocus}
                onEmailChange={this.onEmailChange}
                onTextChange={this.onTextChange}
                previous={this.previous}
                selectedSignatureBlock={selectedSignatureBlock}
              />}
            {this.currentStep() === 'emailSuccess' &&
              <EmailSuccess to={to} cc={cc} onClose={this.onClose} />}
          </Grid>
        </Modal.Content>
      </Modal>
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Document/Contract/Share/index.js